/**
 * Browser errors we never want reported, shared by Sentry and PostHog
 * (SPEC-070) so the two lists cannot drift apart.
 *
 * Messages: Sentry's long-standing ignoreErrors — noise from plugins,
 * extensions and injecting proxies, never from our code.
 *
 * Frames: an error whose every stack frame lives in a browser extension is
 * the extension's, not ours. Only the extension schemes are filtered —
 * deliberately NOT our own allow-list of domains, because a checkout-breaking
 * error can come from Braintree or PayPal scripts on third-party origins and
 * those are exactly the ones PostHog exists to show next to the replay.
 */
export const IGNORED_ERROR_MESSAGES = [
  // Random plugins/extensions
  'top.GLOBALS',
  // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
  'originalCreateNotification',
  'canvas.contentDocument',
  'atomicFindClose',
  // Facebook borked
  'fb_xd_fragment',
  // ISP "optimizing" proxy - `Cache-Control: no-transform` seems
  // to reduce this. (thanks @acdha) See
  // http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
  'bmi_SafeAddOnload',
  'EBCallBackMessageReceived',
  // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
  'conduitPage'
]

export const EXTENSION_FRAME_PATTERN =
  /^(chrome-extension|moz-extension|safari-extension|safari-web-extension|chrome):\/\//i

const matchesIgnoredMessage = (text) =>
  typeof text === 'string' &&
  IGNORED_ERROR_MESSAGES.some((ignored) => text.includes(ignored))

const frameLocation = (frame) => frame?.abs_path || frame?.filename || ''

// posthog-js `before_send`: drops a `$exception` that is only extension or
// plugin noise; every other event, and every exception we might care about,
// passes through untouched. Returning null tells posthog-js not to send.
export const dropIgnoredExceptions = (event) => {
  if (!event || event.event !== '$exception') return event
  const props = event.properties || {}
  const exceptions = Array.isArray(props.$exception_list)
    ? props.$exception_list
    : []

  const messages = [
    props.$exception_message,
    ...exceptions.flatMap((exc) => [exc?.value, exc?.type])
  ]
  if (messages.some(matchesIgnoredMessage)) return null

  const frames = exceptions.flatMap((exc) => exc?.stacktrace?.frames || [])
  if (
    frames.length > 0 &&
    frames.every((frame) => EXTENSION_FRAME_PATTERN.test(frameLocation(frame)))
  )
    return null

  return event
}

// SPEC-077 FR-6: exception payloads are not masked like replays are. Before a
// $exception leaves the browser, cut query strings and fragments off every
// URL (a token or reset code can ride there) and redact email addresses, in
// the messages and in every frame. Other events pass through untouched.
const URL_WITH_QUERY =
  /(\b[a-z][a-z0-9+.-]*:\/\/[^\s?#"'<>)]+)[?#][^\s"'<>)]*/gi
const EMAIL_ADDRESS = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi

export const scrubText = (text) =>
  typeof text === 'string'
    ? text.replace(URL_WITH_QUERY, '$1').replace(EMAIL_ADDRESS, '[email]')
    : text

const scrubLocation = (location) =>
  typeof location === 'string' ? location.replace(/[?#].*$/, '') : location

export const scrubExceptionPii = (event) => {
  if (!event || event.event !== '$exception') return event
  const props = event.properties || {}
  const exceptions = Array.isArray(props.$exception_list)
    ? props.$exception_list.map((exc) => {
        if (!exc) return exc
        const frames =
          exc.stacktrace && Array.isArray(exc.stacktrace.frames)
            ? exc.stacktrace.frames.map((frame) =>
                frame
                  ? {
                      ...frame,
                      filename: scrubLocation(frame.filename),
                      abs_path: scrubLocation(frame.abs_path)
                    }
                  : frame
              )
            : undefined
        return {
          ...exc,
          value: scrubText(exc.value),
          ...(frames && { stacktrace: { ...exc.stacktrace, frames } })
        }
      })
    : props.$exception_list
  return {
    ...event,
    properties: {
      ...props,
      ...(props.$exception_message !== undefined && {
        $exception_message: scrubText(props.$exception_message)
      }),
      ...(exceptions !== undefined && { $exception_list: exceptions })
    }
  }
}
